/**
 * CategoryService - Handles discipline category operations
 * REFACTORED: Now uses BaseRepository with hierarchical collection support
 */

import { BaseRepository } from '../repositories/BaseRepository';

export class CategoryService {
  constructor() {
    // CRITICAL: Enable hierarchical paths - categories stored at schools/{schoolId}/disciplineCategories/{categoryId}
    this.repo = new BaseRepository('disciplineCategories', { isHierarchical: true });
  }

  async createCategory(categoryData) {
    try {
      const category = {
        name: categoryData.name,
        description: categoryData.description || '',
        color: categoryData.color || '#6B7280',
        active: true,
        createdBy: categoryData.createdBy || 'system',
        schoolId: categoryData.schoolId
      };

      return await this.repo.create(category);
    } catch (error) {
      console.error('Error creating discipline category:', error);
      throw error;
    }
  }

  async getCategories(schoolId) {
    try {
      // Guard: require schoolId to prevent permission errors
      if (!schoolId) {
        console.warn('CategoryService: schoolId required but not provided');
        return [];
      }

      // FIXED: Don't filter by 'active' - some categories don't have this field
      // Get all categories and filter client-side if needed
      const categories = await this.repo.getAll(schoolId);

      // Filter out explicitly inactive categories (active=false)
      const activeCategories = categories.filter(cat => cat.active !== false);

      // Sort client-side by name
      return activeCategories.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } catch (error) {
      console.error('[CategoryService] CRITICAL ERROR fetching categories:', error);
      console.error('[CategoryService] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      // If collection doesn't exist yet, return empty array
      return [];
    }
  }

  async updateCategory(categoryId, updates, schoolId) {
    try {
      // For hierarchical collections, schoolId must be in the updates object
      const updateData = schoolId ? { ...updates, schoolId } : updates;
      await this.repo.update(categoryId, updateData);
    } catch (error) {
      console.error('Error updating discipline category:', error);
      throw error;
    }
  }

  async deleteCategory(categoryId, schoolId) {
    try {
      // For hierarchical collections, schoolId must be in the updates object
      const updateData = {
        active: false,
        deletedAt: new Date()
      };
      if (schoolId) {
        updateData.schoolId = schoolId;
      }
      await this.repo.update(categoryId, updateData);
    } catch (error) {
      console.error('Error deleting discipline category:', error);
      throw error;
    }
  }

  subscribeCategories(callback, filters = {}) {
    try {
      console.log('[CategoryService] 🔔 subscribeCategories called with filters:', filters);

      // CRITICAL: Never query without schoolId - would violate security rules
      if (!filters.schoolId) {
        console.error('[CategoryService] Attempted to subscribe without schoolId - returning empty categories');
        callback([]);
        return () => {}; // Return no-op unsubscribe
      }

      console.log('[CategoryService] 🔔 Setting up subscription for schoolId:', filters.schoolId);
      console.log('[CategoryService] 🔔 Repository info:', {
        isHierarchical: this.repo.isHierarchical,
        collectionName: this.repo.collectionName
      });

      // FIXED: Don't filter by 'active' in query - some categories don't have this field
      // Filter client-side instead
      const constraints = [];

      return this.repo.subscribe(
        (categories) => {
          console.log('[CategoryService] 🔔 SUBSCRIPTION CALLBACK FIRED! Raw categories:', categories);
          console.log('[CategoryService] 🔔 Categories count:', categories.length);

          // Filter out explicitly inactive categories (active=false)
          const activeCategories = categories.filter(cat => cat.active !== false);
          console.log('[CategoryService] 🔔 Active categories count:', activeCategories.length);

          // Sort client-side by name
          const sorted = activeCategories.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          console.log('[CategoryService] 🔔 Calling callback with sorted categories:', sorted);
          callback(sorted);
        },
        constraints,
        filters.schoolId
      );
    } catch (error) {
      console.error('[CategoryService] CRITICAL: Error setting up subscription:', error);
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }
  }
}
